import React, { useState, useEffect, useRef } from 'react';
import { FiSave, FiUpload, FiX, FiImage } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { settingsAPI } from '../../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CompanySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [signatureUploading, setSignatureUploading] = useState(false);

  const logoInputRef = useRef(null);
  const signatureInputRef = useRef(null);

  const [formData, setFormData] = useState({
    company_name: '',
    company_address: '',
    company_phone: '',
    company_email: '',
    company_gst: '',
    company_state: '',
    company_logo: '',
    signature_image: '',
    invoice_prefix: 'INV',
    invoice_terms: '',
    low_stock_threshold: 10,
    bank_name: '',
    bank_account_no: '',
    bank_ifsc: '',
    bank_account_holder: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.get();
      if (response.data.data) {
        setFormData(prev => ({
          ...prev,
          ...response.data.data
        }));
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await settingsAPI.update(formData);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Logo upload handlers
  // const handleLogoSelect = async (e) => {
  //   const file = e.target.files[0];
  //   if (!file) return;

  //   const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  //   if (!allowedTypes.includes(file.type)) {
  //     toast.error('Only image files are allowed (JPG, PNG, WEBP)');
  //     return;
  //   }

  //   const maxSize = 2 * 1024 * 1024; // 2MB
  //   if (file.size > maxSize) {
  //     toast.error('File size must be less than 2MB');
  //     return;
  //   }

  //   try {
  //     setLogoUploading(true);
  //     const formDataUpload = new FormData();
  //     formDataUpload.append('logo', file);
  //     const response = await settingsAPI.uploadLogo(formDataUpload);
  //     setFormData(prev => ({ ...prev, company_logo: response.data.data.logo }));
  //     toast.success('Logo uploaded successfully');
  //   } catch (error) {
  //     console.error('Failed to upload logo:', error);
  //     toast.error('Failed to upload logo');
  //   } finally {
  //     setLogoUploading(false);
  //     if (logoInputRef.current) logoInputRef.current.value = '';
  //   }
  // };
  const handleLogoSelect = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    toast.error('Only image files are allowed (JPG, PNG, WEBP)');
    return;
  }

  const maxSize = 2 * 1024 * 1024;
  if (file.size > maxSize) {
    toast.error('File size must be less than 2MB');
    return;
  }

  try {
    setLogoUploading(true);

    const formDataUpload = new FormData();
    // ✅ FIXED KEY
    formDataUpload.append('company_logo', file);

    const response = await settingsAPI.uploadLogo(formDataUpload);

    setFormData(prev => ({
      ...prev,
      company_logo: response.data.data.logo
    }));

    toast.success('Logo uploaded successfully');
  } catch (error) {
    console.error('Failed to upload logo:', error);
    toast.error('Failed to upload logo');
  } finally {
    setLogoUploading(false);
    if (logoInputRef.current) logoInputRef.current.value = '';
  }
};


  const handleLogoDelete = async () => {
    if (!formData.company_logo) return;

    try {
      await settingsAPI.deleteLogo();
      setFormData(prev => ({ ...prev, company_logo: '' }));
      toast.success('Logo deleted successfully');
    } catch (error) {
      console.error('Failed to delete logo:', error);
      toast.error('Failed to delete logo');
    }
  };

  // Signature upload handlers
  const handleSignatureSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG, JPEG, or PNG files are allowed');
      return;
    }

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('File size must be less than 5MB');
      return;
    }

    try {
      setSignatureUploading(true);
      const formDataUpload = new FormData();
      formDataUpload.append('signature_image', file);
      const response = await settingsAPI.uploadSignature(formDataUpload);
      setFormData(prev => ({ ...prev, signature_image: response.data.data.signature }));
      toast.success('Signature uploaded successfully');
    } catch (error) {
      console.error('Failed to upload signature:', error);
      toast.error('Failed to upload signature');
    } finally {
      setSignatureUploading(false);
      if (signatureInputRef.current) signatureInputRef.current.value = '';
    }
  };

  const handleSignatureDelete = async () => {
    if (!formData.signature_image) return;

    try {
      await settingsAPI.deleteSignature();
      setFormData(prev => ({ ...prev, signature_image: '' }));
      toast.success('Signature deleted successfully');
    } catch (error) {
      console.error('Failed to delete signature:', error);
      toast.error('Failed to delete signature');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit}>
        {/* Company Information */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Company Information</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Company Logo */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Logo
              </label>
              <div className="flex items-start gap-4">
                {formData.company_logo ? (
                  <div className="relative">
                    <img
                      src={`${API_URL.replace('/api', '')}${formData.company_logo}`}
                      alt="Company Logo"
                      className="w-24 h-24 object-contain border border-gray-300 rounded-lg bg-white p-2"
                    />
                    <button
                      type="button"
                      onClick={handleLogoDelete}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      title="Remove logo"
                    >
                      <FiX size={14} />
                    </button>
                  </div>
                ) : (
                  <div
                    onClick={() => logoInputRef.current?.click()}
                    className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                  >
                    <FiImage className="text-gray-400 mb-1" size={24} />
                    <span className="text-xs text-gray-500">Upload</span>
                  </div>
                )}
                <input
                  ref={logoInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={handleLogoSelect}
                  className="hidden"
                />
                {formData.company_logo && (
                  <button
                    type="button"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={logoUploading}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <FiUpload size={14} />
                    {logoUploading ? 'Uploading...' : 'Change Logo'}
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">JPG, PNG, WEBP (max 2MB)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                type="text"
                name="company_phone"
                value={formData.company_phone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="company_email"
                value={formData.company_email}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GSTIN
              </label>
              <input
                type="text"
                name="company_gst"
                value={formData.company_gst}
                onChange={handleInputChange}
                maxLength="15"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <input
                type="text"
                name="company_state"
                value={formData.company_state}
                onChange={handleInputChange}
                placeholder="e.g., 07-Delhi"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <textarea
                name="company_address"
                value={formData.company_address}
                onChange={handleInputChange}
                rows="2"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Bank Details */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Bank Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bank Name
              </label>
              <input
                type="text"
                name="bank_name"
                value={formData.bank_name}
                onChange={handleInputChange}
                placeholder="e.g., HDFC Bank, Pahar Ganj - New Delhi"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Number
              </label>
              <input
                type="text"
                name="bank_account_no"
                value={formData.bank_account_no}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                IFSC Code
              </label>
              <input
                type="text"
                name="bank_ifsc"
                value={formData.bank_ifsc}
                onChange={handleInputChange}
                maxLength="11"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Holder Name
              </label>
              <input
                type="text"
                name="bank_account_holder"
                value={formData.bank_account_holder}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Invoice Settings */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Invoice Settings</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Invoice Prefix
              </label>
              <input
                type="text"
                name="invoice_prefix"
                value={formData.invoice_prefix}
                onChange={handleInputChange}
                maxLength="10"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Low Stock Threshold
              </label>
              <input
                type="number"
                name="low_stock_threshold"
                value={formData.low_stock_threshold}
                onChange={handleInputChange}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Invoice Terms
              </label>
              <textarea
                name="invoice_terms"
                value={formData.invoice_terms}
                onChange={handleInputChange}
                rows="3"
                placeholder="Enter default terms and conditions for invoices..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Authorization Signature */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Authorization Signature</h2>
          <p className="text-sm text-gray-500 mb-4">
            This signature will be displayed on all invoices as the authorized signatory.
          </p>

          <div className="flex items-start gap-4">
            {formData.signature_image ? (
              <div className="relative">
                <img
                  src={`${API_URL.replace('/api', '')}${formData.signature_image}`}
                  alt="Authorization Signature"
                  className="max-w-xs max-h-32 border border-gray-300 rounded-lg object-contain bg-white p-2"
                />
                <button
                  type="button"
                  onClick={handleSignatureDelete}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  title="Remove signature"
                >
                  <FiX size={14} />
                </button>
              </div>
            ) : (
              <div
                onClick={() => signatureInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                style={{ minWidth: '200px' }}
              >
                <FiImage className="mx-auto text-gray-400 mb-2" size={32} />
                <p className="text-sm text-gray-600">Click to upload signature</p>
                <p className="text-xs text-gray-400 mt-1">JPG, JPEG, PNG (max 5MB)</p>
              </div>
            )}
            <input
              ref={signatureInputRef}
              type="file"
              accept=".jpg,.jpeg,.png"
              onChange={handleSignatureSelect}
              className="hidden"
            />
            {formData.signature_image && (
              <button
                type="button"
                onClick={() => signatureInputRef.current?.click()}
                disabled={signatureUploading}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
              >
                <FiUpload size={14} />
                {signatureUploading ? 'Uploading...' : 'Change Signature'}
              </button>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <FiSave size={18} />
                Save Settings
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompanySettings;
